import { call, put, select } from 'redux-saga/effects';
import {
  fetchProfilePictureFailure,
  fetchProfilePictureSuccess,
} from '../actions';
import { getProfilePicture } from '../api';
import { FetchProfilePictureRequestAction } from '../types';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { turnMessageOn, updateUserPictures } from 'store/app/actions';
import { logoutRequest, updateCurrentUserPicture } from 'store/auth/actions';
import { getAuthUser } from 'store/auth/selectors';
import { MessageData } from 'store/app/types';
import { renderErrorMessage } from 'utils/render-error-message';

const MISSING_PROFILE_PICTURE_MESSAGE =
  'The file does not exist. Please check the file path and try again.';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchProfilePictureSaga(
  action: FetchProfilePictureRequestAction
) {
  const { userId } = action.payload;
  try {
    let err: ApiError | null;
    const response: any = yield call(getProfilePicture, userId);

    // Check response validity FIRST
    err = checkResponse(response?.responseMessage);
    if (err) throw err;

    // Then safely access data with null checks
    if (!response || !response.data) {
      // Don't throw - just fail silently since profile pictures are not critical
      yield put(fetchProfilePictureFailure());
      return;
    }

    const profilePicture = response.data['profile-picture'];

    yield put(updateUserPictures(profilePicture, userId));

    yield put(fetchProfilePictureSuccess(profilePicture));

    // Update currentUser in auth state if this is the current user's picture
    const currentUser = yield select(getAuthUser);
    if (userId === currentUser?.userUuid) {
      yield put(updateCurrentUserPicture(profilePicture));
    }
  } catch (error: any) {
    if (error?.message === MISSING_PROFILE_PICTURE_MESSAGE) {
      console.error(
        `Profile picture not found for user ${userId}.`,
        error.message
      );
      yield put(fetchProfilePictureFailure());
      return;
    }
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchProfilePictureFailure());
  }
}
